/* eslint-disable @typescript-eslint/no-empty-object-type */
import { ExtendButtonBase, Theme, alpha } from '@material-ui/core';
import MuiButton, { ButtonTypeMap } from '@material-ui/core/Button';

import { OverrideProps } from '@material-ui/core/OverridableComponent';
import * as React from 'react';
import styled, { FlattenSimpleInterpolation, css } from 'styled-components';

type GetColorsProps = Pick<DSButtonProps, 'color' | 'variant'> & { theme: Theme };

export const stripDefaultColors = (
    color: DSButtonProps['color'],
): 'default' | 'inherit' | 'primary' | 'secondary' | undefined => {
    if (!color || color === 'inherit' || color === 'primary' || color === 'secondary' || color === 'default') {
        return color;
    }
    return undefined;
};

const getColorsOutlined = ({ color, theme }: GetColorsProps) => {
    // Inherit and Default do not have a palette in theme
    if (!color || color === 'inherit' || color === 'default') {
        return css``;
    }
    return css`
        color: ${theme.palette[color].main};
        border: 1px solid ${alpha(theme.palette[color].main, 0.5)};
        &.Mui-disabled {
            border: 1px solid ${alpha(theme.palette[color].main, theme.palette.action.disabledOpacity)};
            color: ${alpha(theme.palette[color].main, theme.palette.action.disabledOpacity)};
        }
        &:hover {
            border: 1px solid ${theme.palette[color].main};
            background-color: ${alpha(theme.palette[color].main, theme.palette.action.hoverOpacity)};
            color: ${theme.palette[color].main};
        }
        @media (hover: none) {
            background-color: none;
        }
    `;
};
const getColorsContained = ({ color, theme }: GetColorsProps) => {
    // Inherit and Default do not have a palette in theme
    if (!color || color === 'inherit' || color === 'default') {
        return css``;
    }
    const palette = theme.palette[color];
    return css`
        color: ${palette.contrastText};
        background-color: ${palette.main};
        &.Mui-disabled {
            background-color: ${alpha(palette.main, theme.palette.action.disabledOpacity)};
            color: ${alpha(theme.palette.text.secondary, theme.palette.action.disabledOpacity)};
        }
        &:hover {
            background-color: ${palette.dark};
        }
        @media (hover: none) {
            background-color: ${palette.main};
        }
    `;
};
const getColorsText = ({ color, theme }: GetColorsProps) => {
    // Inherit and Default do not have a palette in theme
    if (!color || color === 'default' || color === 'inherit') {
        return css``;
    }
    const palette = theme.palette[color];
    return css`
        color: ${palette.main};
        &.Mui-disabled {
            color: ${alpha(palette.main, theme.palette.action.disabledOpacity)};
        }
        &:hover {
            background-color: ${alpha(palette.main, theme.palette.action.hoverOpacity)};
        }
        @media (hover: none) {
            background-color: transparent;
        }
    `;
};
export const getStyleForColor = ({ color, theme, variant }: GetColorsProps): FlattenSimpleInterpolation => {
    switch (variant) {
        case 'text':
            return getColorsText({ color, theme });
        case 'outlined':
            return getColorsOutlined({ color, theme });
        default:
            return getColorsContained({ color, theme });
    }
};

export type DSButtonTypeMap<D extends React.ElementType = 'button', P = {}> = Omit<
    ButtonTypeMap<{
        defaultComponent: D;
    }>,
    'props'
> & {
    props: Omit<ButtonTypeMap['props'], 'color'> &
        P & {
            color?: 'default' | 'error' | 'info' | 'inherit' | 'primary' | 'secondary' | 'success' | 'warning';
            ['data-cy']?: string;
        };
};

export type DSButtonProps<D extends React.ElementType = DSButtonTypeMap['defaultComponent'], P = {}> = OverrideProps<
    DSButtonTypeMap<D, P>,
    D
>;

export type DSButtonComponentType<D extends React.ElementType = DSButtonTypeMap['defaultComponent']> =
    ExtendButtonBase<DSButtonTypeMap> & React.FC<DSButtonProps<D>>;

const WrappedMuiButton: DSButtonComponentType = <D extends React.ElementType>({
    color,
    ...props
}: DSButtonProps<D>) => <MuiButton {...props} color={stripDefaultColors(color)} />;
WrappedMuiButton.displayName = 'WrappedMuiButton';

const StyledButton: typeof WrappedMuiButton = styled(WrappedMuiButton)`
    text-transform: none;
    ${({ color, theme, variant }) => getStyleForColor({ color, theme, variant })}
`;

export const DSButton: DSButtonComponentType = <D extends React.ElementType>(props: DSButtonProps<D>) => (
    <StyledButton {...props} />
);
DSButton.displayName = 'DSButton';
